/**
 * When Science sells: by day of the week, by day of the month, and hour ×
 * weekday. Data from /velocity/patterns (backend services/velocity/patterns).
 *
 * "vs its week" is the default because Science is growing fast. A raw
 * weekday average over 13 weeks is mostly a measure of which weekdays fell in
 * the bigger recent weeks; dividing each day by the average of the seven days
 * around it removes the growth and leaves the weekday (or payday) effect.
 * Today is partial and left out of everything.
 */

import { useEffect, useMemo, useState } from 'react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell, ReferenceLine } from 'recharts'
import { Loader2 } from 'lucide-react'
import { velocityAPI } from '../../services/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button } from '../ui'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
// One hue: the bars are one series. Today's weekday takes the darker step.
const BAR = '#2a78d6'
const BAR_TODAY = '#1a4f91'

const METRICS = [
  { key: 'orders', label: 'Orders', format: v => v.toFixed(1) },
  { key: 'revenue', label: 'Revenue', format: v => `$${Math.round(v).toLocaleString()}` },
  { key: 'visitors', label: 'Visitors', format: v => Math.round(v).toLocaleString() },
  { key: 'conversion', label: 'Conversion', format: v => `${(v * 100).toFixed(2)}%` },
  { key: 'spend', label: 'Spend', format: v => `$${Math.round(v).toLocaleString()}`, daily: true },
]

const weekdayOf = day => new Date(`${day}T12:00:00Z`).getUTCDay()
const sum = xs => xs.reduce((a, b) => a + b, 0)

/** One number per complete day for a metric, or null when it has no data. */
function dailyValues(data, metric) {
  const out = {}
  for (const day of data.days) {
    if (day === data.today) continue
    const h = data.hours
    const visitors = sum(h.visitors[day])
    if (metric === 'spend') out[day] = data.spendByDay?.[day] ?? null
    else if (metric === 'conversion') out[day] = visitors > 0 ? sum(h.orders[day]) / visitors : null
    else if (metric === 'revenue') out[day] = h.revenue ? sum(h.revenue[day]) : null
    else out[day] = visitors > 0 || sum(h.orders[day]) > 0 ? sum(h[metric][day]) : null
  }
  return out
}

/** Each day over the mean of the seven days centred on it (needs five). */
function indexed(values, days) {
  const out = {}
  days.forEach((day, i) => {
    if (values[day] == null) return
    const around = days.slice(Math.max(0, i - 3), i + 4).map(d => values[d]).filter(v => v != null)
    if (around.length < 5) return
    const mean = sum(around) / around.length
    if (mean > 0) out[day] = values[day] / mean
  })
  return out
}

function groupBy(values, keyOf, keys) {
  const groups = Object.fromEntries(keys.map(k => [k, []]))
  for (const [day, v] of Object.entries(values)) if (v != null) groups[keyOf(day)]?.push(v)
  return keys.map(k => ({ key: k, n: groups[k].length, value: groups[k].length ? sum(groups[k]) / groups[k].length : null }))
}

function PatternTooltip({ active, payload, format, mode }) {
  if (!active || !payload?.length) return null
  const row = payload[0].payload
  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
      <div className="font-medium">{row.label}{row.today ? ' · today' : ''}</div>
      <div className="tabular-nums">{row.value == null ? '–' : mode === 'index' ? `${Math.round(row.value * 100)}% of its week` : format(row.value)}</div>
      <div className="text-muted-foreground">{row.n} days</div>
    </div>
  )
}

function Bars({ rows, format, mode, height = 220 }) {
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 8, right: 8, left: -8, bottom: 0 }} barCategoryGap="18%">
          <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} interval={0} />
          <YAxis
            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={v => (mode === 'index' ? `${Math.round(v * 100)}%` : format(v))}
            domain={mode === 'index' ? [0, 'auto'] : [0, 'auto']}
          />
          {mode === 'index' && <ReferenceLine y={1} stroke="hsl(var(--muted-foreground))" strokeDasharray="3 3" />}
          <Tooltip content={<PatternTooltip format={format} mode={mode} />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.5 }} />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} isAnimationActive={false}>
            {rows.map(r => <Cell key={r.key} fill={r.today ? BAR_TODAY : BAR} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

/** 7 × 24: average per ET hour of each weekday, one hue light to dark. */
function Heatmap({ data, metric, format }) {
  const cells = useMemo(() => {
    const acc = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => ({ num: 0, den: 0, n: 0 })))
    for (const day of data.days) {
      if (day === data.today) continue
      const w = weekdayOf(day)
      const h = data.hours
      if (sum(h.visitors[day]) === 0 && sum(h.orders[day]) === 0) continue
      for (let hour = 0; hour < 24; hour++) {
        const c = acc[w][hour]
        c.n += 1
        if (metric === 'conversion') { c.num += h.orders[day][hour]; c.den += h.visitors[day][hour] } else { c.num += (h[metric]?.[day]?.[hour]) || 0; c.den += 1 }
      }
    }
    return acc.map(row => row.map(c => (c.den > 0 ? c.num / c.den : null)))
  }, [data, metric])
  const max = Math.max(...cells.flat().filter(v => v != null), 1e-9)
  const todayW = weekdayOf(data.today)

  return (
    <div className="overflow-x-auto">
      <table className="text-[10px] border-separate" style={{ borderSpacing: 2 }}>
        <thead>
          <tr>
            <th />
            {Array.from({ length: 24 }, (_, h) => (
              <th key={h} className="font-normal text-muted-foreground w-7">{h % 3 === 0 ? `${h % 12 || 12}${h < 12 ? 'a' : 'p'}` : ''}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cells.map((row, w) => (
            <tr key={w}>
              <td className={`pr-2 text-xs ${w === todayW ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>{WEEKDAYS[w]}</td>
              {row.map((v, h) => (
                <td
                  key={h}
                  className="h-6 w-7 rounded-sm"
                  style={{ background: v == null ? 'hsl(var(--muted))' : `rgba(42, 120, 214, ${(0.08 + 0.92 * (v / max)).toFixed(3)})` }}
                  title={`${WEEKDAYS[w]} ${h % 12 || 12}${h < 12 ? 'am' : 'pm'}: ${v == null ? 'no data' : format(v)} average`}
                />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        <span>Less</span>
        <span className="h-2 w-24 rounded-sm" style={{ background: 'linear-gradient(to right, rgba(42,120,214,0.08), rgba(42,120,214,1))' }} />
        <span>More · peak {format(max)} an hour</span>
      </div>
    </div>
  )
}

export default function VelocityPatterns() {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [view, setView] = useState('weekday')
  const [metric, setMetric] = useState('orders')
  const [mode, setMode] = useState('index')

  useEffect(() => {
    let live = true
    velocityAPI.getPatterns({ weeks: 13 })
      .then(r => { if (live) setData(r.data.data) })
      .catch(err => { if (live) setError(err.response?.data?.error?.message || err.message) })
    return () => { live = false }
  }, [])

  const metricDef = METRICS.find(m => m.key === metric)
  const available = METRICS.filter(m => !(m.key === 'revenue' && data && !data.hours.revenue) && !(view === 'heatmap' && m.daily))

  const rows = useMemo(() => {
    if (!data || view === 'heatmap') return []
    const values = dailyValues(data, metric)
    const series = mode === 'index' ? indexed(values, data.days.filter(d => d !== data.today)) : values
    if (view === 'weekday') {
      const todayW = weekdayOf(data.today)
      return groupBy(series, weekdayOf, [0, 1, 2, 3, 4, 5, 6]).map(g => ({ ...g, label: WEEKDAYS[g.key], today: g.key === todayW }))
    }
    const todayD = Number(data.today.slice(8, 10))
    return groupBy(series, d => Number(d.slice(8, 10)), Array.from({ length: 31 }, (_, i) => i + 1))
      .map(g => ({ ...g, label: String(g.key), today: g.key === todayD }))
  }, [data, view, metric, mode])

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">Patterns</CardTitle>
            <CardDescription className="text-xs">
              Last 13 weeks, ET, complete days only. {mode === 'index' && view !== 'heatmap' ? 'Each day is measured against the seven days around it, so growth does not decide which day looks best.' : 'Plain averages per day.'}
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-1">
            {[['weekday', 'Day of week'], ['month', 'Day of month'], ['heatmap', 'Hour × weekday']].map(([k, l]) => (
              <Button key={k} size="sm" variant={view === k ? 'default' : 'outline'} onClick={() => setView(k)}>{l}</Button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1 pt-2">
          {available.map(m => (
            <Button key={m.key} size="sm" variant={metric === m.key ? 'secondary' : 'ghost'} className="h-7 text-xs" onClick={() => setMetric(m.key)}>{m.label}</Button>
          ))}
          {view !== 'heatmap' && (
            <span className="ml-auto flex gap-1">
              {[['index', 'vs its week'], ['avg', 'Average']].map(([k, l]) => (
                <Button key={k} size="sm" variant={mode === k ? 'secondary' : 'ghost'} className="h-7 text-xs" onClick={() => setMode(k)}>{l}</Button>
              ))}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {!data && !error && <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading 13 weeks…</div>}
        {data && view === 'heatmap' && (() => {
          // Spend is daily only, so the grid falls back to orders for it.
          const heat = metric === 'spend' ? METRICS[0] : metricDef
          return <Heatmap data={data} metric={heat.key} format={heat.format} />
        })()}
        {data && view !== 'heatmap' && (
          <>
            <Bars rows={rows} format={metricDef.format} mode={mode} height={view === 'month' ? 240 : 220} />
            <details className="mt-2">
              <summary className="text-xs text-muted-foreground cursor-pointer select-none hover:text-foreground">As a table</summary>
              <table className="w-full text-xs mt-2">
                <thead><tr className="text-muted-foreground"><th className="text-left py-1 font-medium">{view === 'weekday' ? 'Day' : 'Date'}</th><th className="text-right py-1 font-medium">{mode === 'index' ? 'vs its week' : 'Average'}</th><th className="text-right py-1 font-medium">Days</th></tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.key} className="border-t">
                      <td className="py-1">{r.label}{r.today ? ' (today)' : ''}</td>
                      <td className="py-1 text-right tabular-nums">{r.value == null ? '–' : mode === 'index' ? `${Math.round(r.value * 100)}%` : metricDef.format(r.value)}</td>
                      <td className="py-1 text-right tabular-nums">{r.n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
            {view === 'month' && <p className="mt-2 text-xs text-muted-foreground">Each date has only about three months behind it, so read single dates loosely.</p>}
          </>
        )}
        {data?.ordersSource === 'bigquery' && <p className="mt-2 text-xs text-muted-foreground">Orders from BigQuery (Manifold unreachable); revenue unavailable.</p>}
        {metric === 'spend' && view !== 'heatmap' && <p className="mt-2 text-xs text-muted-foreground">Hourly spend history starts 2026-09-18, so spend covers fewer days.</p>}
      </CardContent>
    </Card>
  )
}
